import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  Input,
  NgZone,
  OnInit,
  AfterViewInit,
  HostListener
} from '@angular/core';
import {IdynamicFormItem} from '@ispm/dynamic-form/interfaces/IdynamicFormItem';
import {ResolvableComponent} from '@ispm/core/decorators/resolvable-component.decorator';
import {IWidgetItem} from '@ispm/widgets/interfaces/widget-item.interface';
import {cloneDeep, differenceWith, get, has, set} from 'lodash';
import {TherapiesActionCreatorService} from '@ispm/therapies/services/therapies-action-creator.service';
import {Observable, Subject} from 'rxjs';
import {DiagnosticOrderActionsService} from "@ispm/test/services/diagnostic-order-actions.service";
import {pathwaysSelectionJobName} from '@ispm-client/modules/pathways/index';
import {PlaceholderModalComponent} from "@ispm/layout/components/modals/placeholder-modal/placeholder-modal.component";
import {ModalTypes} from "@ispm/layout/components/modals/contants/modal-types.enum";
import {DynamicModalLoaderService} from "@ispm/layout/services/dynamic-modal-loader.service";
import {
  TreatmentPlansSelectionModalComponent
} from "@ispm/therapies/components/treatment-plans-selection-modal/treatment-plans-selection-modal.component";
import {InterpolateService} from "@ispm/shared/services/interpolate.service";
import {FormGroup} from "@angular/forms";
import {filter, take} from "rxjs/operators";
import {FormHandlerService} from "@ispm/dynamic-form/services/form-handler.service";
import {FORM_ACTIONS} from "@ispm/dynamic-form/store/form.actions";
import {Store} from "@ngrx/store";
import {IgenomicsState} from "@ispm/core/interfaces/genomics-state.interface";
import {TunnelResult} from "@ispm/layout/classes/tunnel-result";
import {TestService} from '@ispm/dashboard/test/services/test.service';

interface IPriorityMappingField {
  name: string;
  path: string;
  flat: boolean;
}

interface IPriorityMapping {
  mapDefinerPath: string;
  mapDefinerValue: string;
  fields: IPriorityMappingField[];
}

@Component({
  selector: 'ispm-off-pathway',
  templateUrl: './off-pathway.component.html',
  styleUrls: ['./off-pathway.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default
})

@ResolvableComponent()
export class OffPathwayComponent implements IWidgetItem, OnInit, AfterViewInit {
  public formsConfig: any[] = [];
  public formName: string;
  public widgetUpdateValueCallback: (value) => void;
  public addWidgetFormControl: (config: IdynamicFormItem) => void;
  public selectTreatmentPlansFormTitle: string = '';
  public widgetFormInstance: FormGroup;
  public widgetConfigPayload: any | {};
  public discipline: any = '1';
  public organization: string = '';
  public openSelectTreatmentsIsUsed: boolean = true;
  public forceUpdate: (value) => void;
  public testConfiguration: any;
  public testViewVerticalLayout: boolean;
  public isFormDisabled = false;
  @Input() additionalSearchValues: any;
  private destroyer$: Subject<any> = new Subject<any>();
  private priority: any;
  private selectable: boolean = true;
  private treatments: any[];
  private selectedDataControlName: string = '';
  private formStore: any;
  private treatmentsWasUpdated: boolean = false;
  private lastTouched: string = '';
  private selectedItem: any;

  public disabledOptions: string[];

  constructor(private therapyAC: TherapiesActionCreatorService,
              private diagnosticOrderActionsService: DiagnosticOrderActionsService,
              private testService: TestService,
              private modalLoader: DynamicModalLoaderService,
              private zone: NgZone,
              private changeDetector: ChangeDetectorRef,
              protected formHandler: FormHandlerService,
              private store: Store<IgenomicsState>) {
    this.therapyAC.getAnyResources().takeUntil(this.destroyer$.asObservable())
      .subscribe(resources => {
        this.data = resources || {};
      });
    this.formStore = store.select('forms');
  }

  private _data: any;

  public get data() {
    return this._data;
  }

  @Input()
  public set data(data: any) {
    this._data = data || {};
  }

  set widgetData(priority: any) {
    this.priority = priority;
    this.mapFromPriority(priority);
  }

  public set widgetConfig(widgetConfig: IdynamicFormItem) {
    const widgetPayload = get(widgetConfig, 'widgetPayload', undefined);
    Object.assign(this, widgetPayload);
    this.widgetConfigPayload = widgetPayload;
    this.openSelectTreatmentsIsUsed = get(this.widgetConfigPayload, 'openSelectTreatmentsButtonVisible', true);
  }

  ngOnDestroy() {
    this.destroyer$.next();
    this.destroyer$.complete();
  }

  ngOnInit() {
    this.testConfiguration = this.testService.testConfiguration;
    this.disabledOptions = this.testConfiguration.disabledOptions || [];
    this.testViewVerticalLayout = this.testConfiguration && !!this.testConfiguration.testViewVerticalLayout;
    if (this.data.treatments && this.data.treatments instanceof Array && this.data.treatments.length == 0) {
      this.data._selectedItem = null;
    }
    this.diagnosticOrderActionsService.getState()
      .filter(state => !!state.order && Object.keys(state.order).length > 0)
      .map(state => state.order)
      .takeUntil(this.destroyer$.asObservable())
      .subscribe(order => {
        this.discipline = !!order.discipline && order.discipline.code ? order.discipline.code : this.discipline;
        if (!!this.additionalSearchValues) {
          this.additionalSearchValues.discipline = this.discipline;
          this.organization = order.organization;
        }
        const activeJobPurpose = get(order, 'workflowRequest.activeJobName', "")
        this.isFormDisabled = activeJobPurpose !== pathwaysSelectionJobName;
      });
  }

  ngAfterViewInit(){
    this.setTreatmentPlansListHeight();
  }

  public onItemTouched($event) {
    this.lastTouched = $event;
  }

  @HostListener('window:resize', ['$event'])
  onResize(event) {
    this.resizeTreatmentPlansListDropDown();
  }

  public formChanged($event) {
    this.applyEditReasonChanges($event);
    if (get($event, 'form.treatmentPlansSelection', '').startsWith('Existing')
      && this.lastTouched == 'treatmentsPopup') {
      //Off pathway treatment plans list popup opened
      this.openPopupForTreatmentPlanSelection();
      this.resizeTreatmentPlansListDropDown();
    } else {
      if (get($event, 'updated', [])?.includes('reasonNewLayout')) {
        //Off pathway reason changed
        this.resizeTreatmentPlansListDropDown();
      }

      const form = $event.form;
      const activeTreatment = this.data;
      if (Array.isArray(form._selectedItem)) {
        this.selectedItem = form._selectedItem;
      }
      Object.assign(activeTreatment, form);
      if (this.selectable) {
        const priorityMapper = this.widgetConfigPayload.priorityMapper.find(mapper => get(form, mapper.mapDefinerPath) === mapper.mapDefinerValue);
        if (!!activeTreatment.treatments && activeTreatment.treatments.length === 0) {
          this.data._selectedItem = null;
          this.therapyAC.setUserReviewToInitial();
          this.treatmentsWasUpdated = false;
        }
        this.therapyAC.addTherapy(this.mapToPriority(priorityMapper.fields, activeTreatment) as any, 'id', true);
      }

      // update selected item
      if (has(activeTreatment.reasonNewLayout, 'code')
        && activeTreatment.treatmentPlansSelection.length > 0
        && !!activeTreatment.treatments && activeTreatment.treatments.length > 0
        && !Array.isArray(activeTreatment._selectedItem)) {
        activeTreatment._selectedItem = Array.isArray(this.selectedItem) ? this.selectedItem : [activeTreatment.treatments[activeTreatment.treatments.length - 1]];
      }

      this.therapyAC.setAnyResources(activeTreatment);
      this.bubbleUp(activeTreatment);
    }
  }

  public mapToPriority(conf: IPriorityMappingField[], form) {
    let priority = {'id': 'offPathway'};
    conf.forEach(field => {
      if (field.flat) {
        Object.assign(priority, get(form, field.path));
      } else {
        priority[field.name] = get(form, field.path, null);
      }
    });
    return priority;
  }

  public getFormId(): string {
    if (has(this.widgetConfigPayload, 'formIdByDiscipline') && !!get(this.widgetConfigPayload, 'formIdByDiscipline', undefined)) {
      let formdescr = get(this.widgetConfigPayload, 'formIdByDiscipline').find(formDescr => formDescr.code === this.discipline);
      return !!formdescr ? formdescr.formId : this.widgetConfigPayload.formId;
    }
    return this.widgetConfigPayload.formId;
  }

  public openPopupForTreatmentPlanSelection() {
    // open modal popup, previously selected treatment plans are not selected !
    this.widgetConfigPayload.additionalSearchValues.searchText = '';
    this.selectedDataControlName = get(this.widgetConfigPayload, 'widgetPayload.selectionDataControl', '');
    const payload = {
      searchInput: get(this.widgetConfigPayload, 'selectTreatmentsPopup.searchInput'),
      url: InterpolateService.interpolate(get(this.widgetConfigPayload, 'selectTreatmentsPopup.url'), this),
      selectAllEnabled: get(this.widgetConfigPayload, 'selectTreatmentsPopup.selectAllEnabled'),
      table: get(this.widgetConfigPayload, 'selectTreatmentsPopup.table'),
      pagination: get(this.widgetConfigPayload, 'selectTreatmentsPopup.pagination'),
      pageSize: get(this.widgetConfigPayload, 'selectTreatmentsPopup.pageSize'),
      searchField: get(this.widgetConfigPayload, 'selectTreatmentsPopup.searchField'),
      alreadySelected: this.data.treatments || [],
      exactMultiSearch: true
    };

    const subscriber: Observable<TunnelResult> = this.modalLoader.popupModal(PlaceholderModalComponent, {
      title: get(this.widgetConfigPayload, 'selectTreatmentsPopup.title', this.selectTreatmentPlansFormTitle),
      componentToLoad: TreatmentPlansSelectionModalComponent,
      classes: 'treatment-plans-modal-container ',
      buttons: {
        approveBtn: 'Shortlist',
        declineBtn: 'Cancel'
      },
      emitCancel: true,
      payload: payload,
      approveCallback: (result) => {
        const selectedRows = result.selectedRows;
        this.treatmentsWasUpdated = result.status == 1 ? (!!selectedRows && selectedRows.length !== this.data.treatments ||
          differenceWith(selectedRows, this.data.treatments,
            (el, tr) => get(el, 'id') === get(tr, 'id')).length > 0) : false;
        return Observable.of(selectedRows);
      },
    }, ModalTypes.NONE);

    if (!!subscriber) {
      subscriber.subscribe((response: any) => {
        this.lastTouched = '';
        if (response.payload && response.payload.status === 1 && response.payload.data && this.treatmentsWasUpdated) {
          this.data.treatments = response.payload.data;
          this.updateTreatments(this.data.treatments);
          this.formChanged({form: this.data, updated: null});
        }
      });
    }
  }

  updateValue($event) {
    if (!!this.selectedDataControlName && !!this.widgetFormInstance.controls[this.selectedDataControlName]) {
      const selectedItem = this.widgetFormInstance.controls[this.selectedDataControlName].value;
      set(this.data, this.selectedDataControlName, selectedItem);
      this.formChanged({form: this.data, updated: null});
    }
  }

  private applyEditReasonChanges($event: any) {
    // If the show edit reason not part of the current form , then return
    if ($event.form?.showEditReason == undefined) {
      return;
    }

    let reasonSelection = get($event, 'form.reasonNewLayout.code', null);

    // Show edit reason panel only If
    // 1- edit reason button has been clicked
    // 2- Selecting the reason layout to be "other reason"
    if ((this.lastTouched == 'reasonNewLayout' && reasonSelection != null
      && reasonSelection == 'Other Reason') || this.lastTouched == 'editReasonButton') {
      $event.form.showEditReason = true;
    } else {
      $event.form.showEditReason = false;
    }

    // Clear edit reason extra details if reason selection changed
    if (reasonSelection != null && reasonSelection != 'Other Reason') {
      this.data.reasonExtraDetails = null;
    }
  }

  private bubbleUp(activeTreatment) {
    if (this.widgetConfigPayload.applyWidgetUpdateValueCallback) {
      this.forceUpdate(true);
      this.widgetUpdateValueCallback(activeTreatment);
    }
  }

  private mapFromPriority(priority: any) {
    if (this.widgetConfigPayload.priorityIdentifier && get(priority, this.widgetConfigPayload.priorityIdentifier.path) === this.widgetConfigPayload.priorityIdentifier.value) {
      this.data = get(priority, this.widgetConfigPayload.priorityFormPath);
      this.formChanged({form: this.data, updated: null});
      this.selectable = false;
    }
  }

  private updateTreatments(selectedRows) {
    this.data.treatments = selectedRows;
    if (this.data.treatments) {
      if (this.data.treatments.length > 0) {
        this.data._selectedItem = [this.data.treatments[this.data.treatments.length - 1]];
      } else {
        this.data._selectedItem = null;
      }
      const formId = this.getFormId();
      this.formHandler.getStoredFormById(formId)
        .pipe(
          filter(form => !!form),
          take(1))
        .subscribe((formdata) => {
          this.formStore.dispatch({
            type: FORM_ACTIONS.ADD_FORM,
            payload: {
              name: formId,
              inputs: cloneDeep(formdata)
            }
          });
        });
      this.forceUpdate(true);
      this.changeDetector.markForCheck();
    }
  }

  private resizeTreatmentPlansListDropDown() {
    setTimeout(() => {
      this.setTreatmentPlansListHeight();
    }, 200);
  }

  private setTreatmentPlansListHeight() {
    const appRootEl = document.getElementsByTagName('app-root')[0] as HTMLElement;
    const ispmNotifEl = appRootEl.getElementsByTagName('ispm-notification')[0] as HTMLElement;
    const genHomeEl = appRootEl.getElementsByTagName('gen-home')[0] as HTMLElement;
    const mainHeaderEl = genHomeEl.getElementsByTagName('header')[0] as HTMLElement;
    const genProfileV2El = genHomeEl.getElementsByTagName('gen-profile-v2')[0] as HTMLElement;
    const subHeaderEl = genProfileV2El.getElementsByTagName('header')[0] as HTMLElement;
    const testViewBarEl = genProfileV2El.getElementsByClassName('test-view-bar')[0] as HTMLElement;
    const testTabEl = genProfileV2El.getElementsByTagName('gen-test-tab')[0] as HTMLElement;

    let leftPanelHeight = this.calculateHeight(appRootEl.getBoundingClientRect().height, [ispmNotifEl, mainHeaderEl, subHeaderEl, testViewBarEl, testTabEl]);
    const leftPanelEl = appRootEl.getElementsByClassName('left-panel')[0] as HTMLElement;
    if (leftPanelEl) {
      const navPanelEl = leftPanelEl.getElementsByClassName('navigation_panel')[0] as HTMLElement;
      const notePanelEl = leftPanelEl.getElementsByClassName('dynamic-form-item-named-note')[0] as HTMLElement;
      const tabsPanelEl = leftPanelEl.getElementsByTagName('gen-tabs-header')[0] as HTMLElement;
      const offPathReasonEl = leftPanelEl.getElementsByClassName('dynamic-form-item-named-reasonNewLayout')[0] as HTMLElement;
      const offPathReasonExtraEl = leftPanelEl.getElementsByClassName('dynamic-form-item-named-reasonExtraDetails')[0] as HTMLElement;
      const offPathTreatmentSelectionEl = leftPanelEl.getElementsByClassName('dynamic-form-item-named-treatmentPlansSelection')[0] as HTMLElement;

      let heightOffset = 5;
      let minHeight = 120;
      let height = this.calculateHeight(leftPanelHeight, [navPanelEl, notePanelEl, tabsPanelEl, offPathReasonEl, offPathReasonExtraEl, offPathTreatmentSelectionEl]);
      const offPathListItem = leftPanelEl.getElementsByTagName('ol')[0] as HTMLElement;
      if (offPathListItem) {
        offPathListItem.setAttribute('style', 'height: ' + (height - heightOffset) + 'px; overflow-y: auto; min-height: ' + minHeight + 'px');
      }
    }
  }

  private calculateHeight(height: number, elements: Array<HTMLElement>) {
    elements.forEach((el) => {
      if (el) {
        height = height - el.getBoundingClientRect().height;
      }
    });
    return height;
  }
}
